/**
 * The whole [data-product-single-media-wrapper] node (not just the inner
 * deferred-media element) has to move, because deferred-media resolves its
 * mediaContainer via `this.closest('[data-media-id]')`, and that attribute
 * lives on the wrapper.
 *
 * Listeners are bound on the item container (not just the poster button),
 * because once a video has loaded once, deferred-media hides its poster
 * button and the bare <video> element becomes the click target instead.
 */
export function initBirdsnestTvVideoModal() {
  const items = document.querySelectorAll('[data-birdsnest-tv-video]');
  if (items.length === 0) return;

  const getModal = () =>
    document.querySelector('mr-modal#birdsnest-tv-video-modal');

  let modal = getModal();

  if (!modal) {
    customElements.whenDefined('mr-modal').then(() => {
      modal = getModal();
      if (modal) attachListeners(modal);
    });
  } else {
    attachListeners(modal);
  }

  function attachListeners(modalElement) {
    const mediaSlot = modalElement.querySelector(
      '[data-birdsnest-tv-modal-media]'
    );
    const closeButton = mediaSlot.querySelector('[data-popup-close]');

    let activeWrapper = null;
    let activePlaceholder = null;

    items.forEach((item) => {
      const wrapper = item.querySelector('[data-product-single-media-wrapper]');
      if (!wrapper) return;

      let touchStart = null;
      let ignoreNextClick = false;

      item.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) {
            touchStart = null;
            return;
          }

          const touch = event.touches[0];
          touchStart = {
            x: touch.clientX,
            y: touch.clientY,
          };
        },
        { capture: true, passive: true }
      );

      item.addEventListener(
        'touchend',
        (event) => {
          if (!touchStart || event.changedTouches.length !== 1) return;

          const touch = event.changedTouches[0];
          const movedX = Math.abs(touch.clientX - touchStart.x);
          const movedY = Math.abs(touch.clientY - touchStart.y);
          touchStart = null;

          // Leave horizontal/vertical swipes to Flickity and page scrolling.
          if (movedX > 10 || movedY > 10) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          ignoreNextClick = true;
          window.setTimeout(() => {
            ignoreNextClick = false;
          }, 700);
          openVideo(wrapper);
        },
        { capture: true, passive: false }
      );

      item.addEventListener(
        'touchcancel',
        () => {
          touchStart = null;
        },
        true
      );

      item.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();

          // iOS can dispatch a synthetic click after touchend.
          if (ignoreNextClick) {
            ignoreNextClick = false;
            return;
          }

          openVideo(wrapper);
        },
        true
      );
    });

    function openVideo(wrapper) {
      const placeholder = document.createComment('birdsnest-tv-video');
      wrapper.before(placeholder);
      activeWrapper = wrapper;
      activePlaceholder = placeholder;

      // The inline thumbnail forces a cropped box via padding-top; in the
      // modal we fix the height via CSS instead and drive the width from
      // the video's true aspect ratio so nothing gets cropped.
      const deferredMedia = wrapper.querySelector('[data-deferred-media]');
      const trueAspectRatio = parseFloat(wrapper.dataset.aspectratio);
      if (deferredMedia && trueAspectRatio) {
        deferredMedia.style.setProperty(
          '--birdsnest-tv-modal-aspect-ratio',
          trueAspectRatio
        );
      }

      mediaSlot.insertBefore(wrapper, closeButton);
      modalElement.show();

      if (!deferredMedia) return;

      if (deferredMedia.getAttribute('loaded')) {
        const video = deferredMedia.querySelector('video');
        if (video) video.play();
      } else {
        deferredMedia.loadContent();
      }
    }

    document.addEventListener('theme:mr-modal:closed', (event) => {
      if (event.detail?.modal !== modalElement || !activeWrapper) return;

      const video = activeWrapper.querySelector('video');
      if (video) video.pause();
      const iframe = activeWrapper.querySelector('iframe');
      if (iframe) {
        // Stop YouTube/Vimeo playback by clearing and restoring the src.
        const src = iframe.src;
        iframe.src = 'about:blank';
        iframe.src = src;
      }

      if (activePlaceholder) {
        activePlaceholder.replaceWith(activeWrapper);
      }

      activeWrapper = null;
      activePlaceholder = null;
    });
  }
}
